import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { ApplicationContextService } from "../services/application-context.service";
import { JwtHelperService } from "@auth0/angular-jwt";
import { filter, take, switchMap, catchError } from "rxjs/operators";
import { JwtAuthService } from "./jwt-auth.service";
const jwtHelper = new JwtHelperService();
@Injectable()
export class JwtAuthInterceptor implements HttpInterceptor {

  clientId: string = '2FC6E2AC-18D6-462E-A662-FF6BC75968C9';

  constructor(
    private context: ApplicationContextService,
    private jwtAuthService: JwtAuthService
  ) { }

  private refreshInProgress = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    //don't include bearer tokens in external requests
    if (req.headers.has('externalRequest')) {

      return next.handle(this.externalRequest(req));

    }

    //allows requests to be sent without a bearer token. E.g when refreshing a token
    if (req.headers.has('skipAuth')) {

      return next.handle(this.noAuthRequest(req));

    }

    //include api gateway key for APIM requests
    if (req.headers.has('apimRequest')) {

      return next.handle(this.apimRequest(req));

    }

    //if the app is embedded in a JWT Auth Context
    if (this.context.isJwtContext()) {

      let token = this.jwtAuthService.getJwt();

      if (!token) {
        return next.handle(this.handleFailure(req));
      }

      console.log('--JWT Expires-- ' + jwtHelper.getTokenExpirationDate(token));

      //if JWT has expired
      if (jwtHelper.isTokenExpired(token)) {
        return this.refreshExpiredToken(req, token, next);
      }

      return next.handle(this.jwtRequest(req, token));

    }

    return next.handle(req);

  }

  refreshExpiredToken(req: HttpRequest<any>, expiredToken: string, next: HttpHandler): Observable<HttpEvent<any>> {
    //if a request to refresh the expired JWT is already in progress
    if (this.refreshInProgress) {

      console.log('--JWT--refreshInProgress');

      return this.refreshTokenSubject
        .pipe(
          filter(result => result !== null),
          take(1),
          switchMap((token) => {
            console.log('--JWT--new token retrieved: ' + token)
            return next.handle(this.jwtRequest(req, token))
          })
        )

    } else {

      console.log('--JWT-- token expired');

      this.refreshInProgress = true;

      // Set the refreshTokenSubject to null so that subsequent API calls will wait until the new token has been retrieved
      this.refreshTokenSubject.next(null);

      console.log('--JWT-- refreshing token...');

      return this.jwtAuthService.refreshToken()
        .pipe(
          catchError((e) => {
            return of(undefined)
          }),
          switchMap(token => {
            this.refreshInProgress = false;
            if (token == 'noRefreshToken' || !token) {
              return next.handle(this.handleFailure(req))
            } else {
              this.refreshTokenSubject.next(token);
              return next.handle(this.jwtRequest(req, token))
            }
          })
        )

    }
  }

  jwtRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {

    return request.clone({
      url: request.url,
      headers: new HttpHeaders({
        "Authorization": "Bearer " + token,
        "Accept": 'application/json',
        "X-Client-Id": this.clientId
      })
    });
  }

  externalRequest(request: HttpRequest<any>): HttpRequest<any> {

    return request.clone({
      headers: new HttpHeaders({})
    });

  }

  noAuthRequest(request: HttpRequest<any>): HttpRequest<any> {

    return request.clone({
      headers: new HttpHeaders({
        'X-Client-Id': this.clientId
      })
    });

  }

  apimRequest(request: HttpRequest<any>): HttpRequest<any> {

    return request;

  }

  handleFailure = (request: HttpRequest<any>): HttpRequest<any> => {

    this.jwtAuthService.forceParentLogout();
    return this.noAuthRequest(request);

  }

}